import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RuleHistoryDialog from "@/components/RuleHistoryDialog";
import {
  Wrench, Plus, Loader2, Play, Trash2, CheckCircle2, XCircle, AlertTriangle, Zap, History,
} from "lucide-react";

const CATEGORIES = [
  "labor","drive_time","materials","chemicals","fuel","mileage",
  "equipment","subcontractor","disposal","permits",
  "credit_card_expenses","payroll_burden","overhead","other",
];
const SOURCES = [
  "estimate_import","manual","time_clock","wage_config","inventory_usage",
  "mileage_source","equipment_rental","purchase_order",
];

export default function AutoCaptureRulesPanel() {
  const [rules, setRules] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [historyRule, setHistoryRule] = useState(null);
  const [draft, setDraft] = useState({
    kind: "", label: "", category: "labor", source: "time_clock",
    hourly_rate: "", per_mile: "", enabled: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/job-costing/auto-capture/rules");
      setRules(data.rules || []);
      setAdapters(data.adapters || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rules");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.kind || !draft.label) { toast.error("Pick an adapter and label"); return; }
    const config = {};
    if (draft.hourly_rate) config.hourly_rate = Number(draft.hourly_rate);
    if (draft.per_mile) config.per_mile = Number(draft.per_mile);
    try {
      await api.post("/job-costing/auto-capture/rules", {
        kind: draft.kind, label: draft.label, category: draft.category,
        source: draft.source, config, enabled: true,
      });
      toast.success("Rule created");
      setCreating(false);
      setDraft({ kind:"", label:"", category:"labor", source:"time_clock", hourly_rate:"", per_mile:"", enabled:true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create rule"); }
  };
  const toggle = async (r) => {
    try {
      await api.patch(`/job-costing/auto-capture/rules/${r.id}`, {
        kind: r.kind, label: r.label, category: r.category, source: r.source,
        config: r.config || {}, enabled: !r.enabled,
      });
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const remove = async (rid) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/job-costing/auto-capture/rules/${rid}`);
    toast.success("Deleted");
    load();
  };
  const testRule = async (rid) => {
    try {
      const { data } = await api.post(`/job-costing/auto-capture/rules/${rid}/test`);
      if (data.ok) {
        toast.success(`Test OK — ${data.row_count} row(s), would total $${(data.would_total || 0).toFixed(2)}`);
      } else {
        toast.error(`Test failed — ${data.error}`);
      }
    } catch (e) { toast.error("Test failed"); }
  };

  if (loading) return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6 card-crisp"><Loader2 className="w-4 h-4 animate-spin"/></div>
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp mb-6" data-testid="auto-capture-panel">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#FF5A00]"/> Auto-capture rules
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">Pull labor, mileage, materials, fuel, and more onto job cost sheets automatically.</p>
        </div>
        <Button onClick={() => setCreating(!creating)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="ac-new-rule-btn">
          <Plus className="w-4 h-4 mr-1"/> New rule
        </Button>
      </div>

      {creating && (
        <div className="p-6 bg-orange-50 border-b border-orange-100 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ac-new-rule-form">
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Adapter</label>
            <Select value={draft.kind} onValueChange={(v)=>setDraft({...draft, kind:v})}>
              <SelectTrigger data-testid="ac-adapter-select"><SelectValue placeholder="Pick a source…"/></SelectTrigger>
              <SelectContent>{adapters.map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Label</label>
            <Input value={draft.label} onChange={(e)=>setDraft({...draft, label:e.target.value})} placeholder="Field labor from time clock" data-testid="ac-label-input"/>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Category</label>
            <Select value={draft.category} onValueChange={(v)=>setDraft({...draft, category:v})}>
              <SelectTrigger data-testid="ac-category-select"><SelectValue/></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Source</label>
            <Select value={draft.source} onValueChange={(v)=>setDraft({...draft, source:v})}>
              <SelectTrigger data-testid="ac-source-select"><SelectValue/></SelectTrigger>
              <SelectContent>{SOURCES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Hourly rate (labor/equipment)</label>
            <Input type="number" step="0.01" value={draft.hourly_rate} onChange={(e)=>setDraft({...draft, hourly_rate:e.target.value})} placeholder="50.00" data-testid="ac-hourly-rate"/>
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono-accent text-zinc-500">Per mile (mileage)</label>
            <Input type="number" step="0.01" value={draft.per_mile} onChange={(e)=>setDraft({...draft, per_mile:e.target.value})} placeholder="0.67" data-testid="ac-per-mile"/>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button onClick={()=>setCreating(false)} variant="ghost" data-testid="ac-cancel-btn">Cancel</Button>
            <Button onClick={create} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="ac-save-btn">Save rule</Button>
          </div>
        </div>
      )}

      {rules.length === 0 ? (
        <div className="p-8 text-center text-zinc-400 text-sm">No rules yet. Add one to start capturing costs automatically.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 border-b border-zinc-100">
            <tr><th className="text-left px-6 py-3">Rule</th><th>Adapter</th><th>Category</th><th>Last run</th><th>Last result</th><th></th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 last:border-0" data-testid={`ac-rule-${r.id}`}>
                <td className="px-6 py-3">
                  <div className="font-medium">{r.label}</div>
                  <label className="inline-flex items-center gap-2 mt-1 text-xs text-zinc-500">
                    <input type="checkbox" checked={r.enabled} onChange={()=>toggle(r)} className="accent-[#FF5A00]" data-testid={`ac-toggle-${r.id}`}/>
                    {r.enabled ? "Enabled" : "Disabled"}
                  </label>
                </td>
                <td className="text-center text-xs text-zinc-600 font-mono-accent">{r.kind}</td>
                <td className="text-center text-xs text-zinc-600">{r.category}</td>
                <td className="text-center text-xs text-zinc-500">{r.last_run_at ? new Date(r.last_run_at).toLocaleString() : "—"}</td>
                <td className="text-center">
                  {r.last_result === "ok" ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5"/> ok · {r.last_rows}</span> :
                   r.last_result === "error" ? <span className="inline-flex items-center gap-1 text-rose-700 text-xs" title={r.last_error}><XCircle className="w-3.5 h-3.5"/> error</span> :
                   <span className="text-xs text-zinc-400">—</span>}
                  {r.error_history?.length > 0 && (
                    <div className="text-[10px] text-rose-500 mt-0.5"><AlertTriangle className="w-2.5 h-2.5 inline"/> {r.error_history.length} recent</div>
                  )}
                </td>
                <td className="text-right px-6 py-3">
                  <button onClick={()=>setHistoryRule(r)} className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded mr-1" data-testid={`ac-history-${r.id}`}>
                    <History className="w-3 h-3"/> History
                  </button>
                  <button onClick={()=>testRule(r.id)} className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded mr-1" data-testid={`ac-test-${r.id}`}>
                    <Play className="w-3 h-3"/> Test
                  </button>
                  <button onClick={()=>remove(r.id)} className="text-xs inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded" data-testid={`ac-delete-${r.id}`}>
                    <Trash2 className="w-3 h-3"/> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RuleHistoryDialog
        rule={historyRule}
        open={!!historyRule}
        onOpenChange={(v) => !v && setHistoryRule(null)}
        onChanged={load}
      />
    </div>
  );
}
